<template>
  <div class="c-explore-new-projects">
    <div class="o-width-limiter--full">
    <!-- Section Heading -->
    <h2 class="c-explore-new-projects__heading">
      {{ parsedHeading }}
    </h2>

    <!-- Web Component Carousel -->
    <slide-list
      v-if="mappedItems.length > 0"
      :id="id"
      class="c-explore-new-projects__carousel"
      data-slider-selector=".slider"
      data-button-previous-selector=""
      data-button-next-selector=""
    >
      <!-- Slider Container -->
      <div class="slider">
        <!-- Project Cards using VObject -->
        <div
          v-for="(item, index) in mappedItems"
          :key="`project-${index}`"
          class="slider__item"
          :id="`${id}-item-${index}`"
        >
          <template v-if="item.variant === 'object'">
            <v-object
              :url="item.url"
              :title="item.title"
              :address="item.address"
              :images="item.images"
              :image="item.image"
              :size-from="item.sizeFrom"
              :size-to="item.sizeTo"
              :premise-types="item.premiseTypes"
              :icon-label="item.iconLabel"
              :number-of-workplaces="item.numberOfWorkplaces"
              :min-number-of-workplaces="item.minNumberOfWorkplaces"
              :max-number-of-workplaces="item.maxNumberOfWorkplaces"
              :price-per-square="item.pricePerSquare"
              :city="item.city"
              :district="item.district"
              :features="item.features"
              :selected-features="item.selectedFeatures"
              :overflow-features-count="item.overflowFeaturesCount"
              :show-navigation="true"
              :show-buttons="true"
            />
          </template>
          <template v-else-if="item.variant === 'card'">
            <v-object-card
              :url="item.url"
              :title="item.title"
              :address="item.address"
              :images="item.images"
              :image="item.image"
              :size-from="item.sizeFrom"
              :size-to="item.sizeTo"
              :premise-types="item.premiseTypes"
              :icon-label="item.iconLabel"
              :number-of-workplaces="item.numberOfWorkplaces"
              :min-number-of-workplaces="item.minNumberOfWorkplaces"
              :max-number-of-workplaces="item.maxNumberOfWorkplaces"
              :price-per-square="item.pricePerSquare"
              :city="item.city"
              :district="item.district"
              :features="item.features"
              :selected-features="item.selectedFeatures"
              :overflow-features-count="item.overflowFeaturesCount"
              :show-navigation="true"
              :show-buttons="true"
            />
          </template>
          <template v-else-if="item.variant === 'puff'">
            <v-object-puff
              :project-image="item.projectImage"
              :project-title="item.projectTitle"
              :project-description="item.projectDescription"
              :project-cta-link="item.projectCtaLink"
              :project-cta-text="item.projectCtaText"
            />
          </template>
        </div>
      </div>
    </slide-list>

    <!-- Bottom Bar: Navigation Controls (left) + CTA Button (right) -->
    <div
      v-if="mappedItems.length > 0"
      class="c-explore-new-projects__bottom-bar"
    >
      <!-- Navigation Controls -->
      <div class="c-explore-new-projects__nav-controls">
        <!-- Previous Button -->
        <button
          class="c-explore-new-projects__nav-arrow c-explore-new-projects__nav-arrow--prev"
          @click="previousPage"
          :disabled="currentPage === 0"
          aria-label="Previous project"
        >
          <i
            class="fa-regular fa-arrow-left c-explore-new-projects__nav-arrow-icon"
          ></i>
        </button>

        <!-- Pagination Indicator -->
        <div
          class="c-explore-new-projects__pagination"
          role="presentation"
          aria-label="Projects pagination"
        >
          <div
            class="c-explore-new-projects__progress"
            @click="handleProgressClick"
          >
            <div
              class="c-explore-new-projects__progress-fill"
              :style="progressStyle"
            ></div>
          </div>
        </div>

        <!-- Next Button -->
        <button
          class="c-explore-new-projects__nav-arrow c-explore-new-projects__nav-arrow--next"
          @click="nextPage"
          :disabled="currentPage === totalPages - 1"
          aria-label="Next project"
        >
          <i
            class="fa-regular fa-arrow-right c-explore-new-projects__nav-arrow-icon"
          ></i>
        </button>
      </div>

      <!-- CTA Button -->
      <div
        v-if="parsedButtonLink && parsedButtonText"
        class="c-explore-new-projects__footer"
      >
        <a :href="parsedButtonLink" class="c-explore-new-projects__cta-button">
          {{ parsedButtonText }}
        </a>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import VObject from "@/components/object/VObject.vue";
import VObjectCard from "@/components/object-card/VObjectCard.vue";
import VObjectPuff from "@/components/object-puff/VObjectPuff.vue";

export default {
  name: "VExploreNewProjects",
  components: {
    VObject,
    VObjectCard,
    VObjectPuff,
  },
  props: {
    heading: {
      type: [String, Object],
      default: "Utforska fler lokaler i samma område",
    },
    items: {
      type: [Array, String],
      default: () => [],
    },
    buttonText: {
      type: [String, Object],
      default: "Se alla lediga lokaler",
    },
    buttonLink: {
      type: [String, Object],
      default: null,
    },
    itemsPerPage: {
      type: Number,
      default: null, // Will be set dynamically based on screen size
    },
    id: {
      type: String,
      default: () =>
        `explore-projects-${Math.random().toString(36).substr(2, 9)}`,
    },
    variant: {
      type: String,
      default: "object",
      validator: (v) => ["object", "card", "teaser", "puff"].includes(v),
    },
  },
  data() {
    return {
      activeItemIndex: 0,
      prefersReducedMotion: false,
      responsiveItemsPerPage: 3,
    };
  },
  computed: {
    effectiveItemsPerPage() {
      // Use prop value if explicitly set, otherwise use responsive value
      return this.itemsPerPage !== null
        ? this.itemsPerPage
        : this.responsiveItemsPerPage;
    },
    parsedHeading() {
      return this.heading;      
    },
    parsedItems() {
      if (typeof this.items === "string") {
        try {
          return JSON.parse(this.items);
        } catch (e) {
          return [];
        }
      }
      return Array.isArray(this.items) ? this.items : [];
    },
    parsedButtonText() {
      if (typeof this.buttonText === "string") {
        try {
          return JSON.parse(this.buttonText);
        } catch (e) {
          return this.buttonText;
        }
      }
      return this.buttonText;
    },
    parsedButtonLink() {
      if (typeof this.buttonLink === "string") {
        try {
          return JSON.parse(this.buttonLink);
        } catch (e) {
          return this.buttonLink;
        }
      }
      return this.buttonLink;
    },
    /**
     * Map items to VObject-compatible props.
     * Supports both CMS format (Heading, Preamble, Images, Link, etc.)
     * and object format (title, address, images, url, etc.)
     */
    mappedItems() {
      return this.parsedItems.map((item) => {
        // Map images from CMS format if needed
        let images = item.images || item.Images || [];
        // Normalize image objects to have consistent imgSrc and AltText properties (what VObject expects)
        if (images && images.length > 0) {
          images = images.map((img) => ({
            imgSrc: img.imgSrc || img.ImgSrc || img.src || img.Url || img.url,
            AltText:
              img.AltText ||
              img.altText ||
              img.Alt ||
              img.alt ||
              item.Heading ||
              item.title ||
              "",
          }));
        }

        // Map premise types from CMS format if needed
        let premiseTypes = item.premiseTypes || [];
        if ((!premiseTypes || premiseTypes.length === 0) && item.PremiseTypes) {
          premiseTypes = item.PremiseTypes;
        }

        const projectImageAlt =
          (item.projectImage && item.projectImage.alt) ||
          (item.projectImage && item.projectImage.AltText) ||
          (item.ProjectImage && item.ProjectImage.alt) ||
          (item.ProjectImage && item.ProjectImage.AltText) ||
          (item.project_image && item.project_image.alt) ||
          (item.project_image && item.project_image.AltText) ||
          (item.Image && item.Image.alt) ||
          (item.Image && item.Image.AltText) ||
          item.projectTitle ||
          item.title ||
          item.Heading ||
          "";

        const projectImageSrc =
          (item.projectImage && item.projectImage.src) ||
          (typeof item.projectImage === "string" ? item.projectImage : null) ||
          (item.ProjectImage && item.ProjectImage.src) ||
          (item.project_image && item.project_image.src) ||
          (item.Image && item.Image.src) ||
          (images[0] && images[0].imgSrc) ||
          null;

        const projectImage = projectImageSrc
          ? { src: projectImageSrc, alt: projectImageAlt }
          : null;

        return {
          variant: item.variant || this.variant,
          projectImage,
          projectTitle:
            item.projectTitle || item.title || item.Heading || "Object",
          projectDescription:
            item.projectDescription || item.description || item.preamble || "",
          projectCtaLink: item.projectCtaLink || item.Link || item.url || "/",
          projectCtaText:
            item.projectCtaText ||
            item.ctaText ||
            item.CtaText ||
            item.buttonText ||
            item.ButtonText ||
            "",
          url: item.url || item.Link || "/",
          title: item.title || item.Heading || "Object",
          address: item.address || item.Preamble || item.Address || "",
          images: images,
          image:
            item.image
              ? {
                  imgSrc: item.image.imgSrc || item.image.ImgSrc || item.image.src || item.image.Url || item.image.url,
                  AltText: item.image.AltText || item.image.altText || item.image.alt || item.image.Alt || "",
                }
              : item.Image
              ? {
                  imgSrc: item.Image.imgSrc || item.Image.ImgSrc || item.Image.src || item.Image.Url || item.Image.url,
                  AltText: item.Image.AltText || item.Image.altText || item.Image.alt || item.Image.Alt || "",
                }
              : undefined,
          sizeFrom: item.sizeFrom || item.SizeFrom || undefined,
          sizeTo: item.sizeTo || item.SizeTo || undefined,
          premiseTypes: premiseTypes,
          iconLabel: item.iconLabel || item.IconLabel || undefined,
          numberOfWorkplaces:
            item.numberOfWorkplaces || item.NumberOfWorkplaces || undefined,
          minNumberOfWorkplaces:
            item.minNumberOfWorkplaces ||
            item.MinNumberOfWorkplaces ||
            undefined,
          maxNumberOfWorkplaces:
            item.maxNumberOfWorkplaces ||
            item.MaxNumberOfWorkplaces ||
            undefined,
          pricePerSquare:
            item.pricePerSquare ||
            item.PricePerSquare ||
            item.PricePerSqure ||
            undefined,
          city: item.city || item.City || undefined,
          district: item.district || item.District || undefined,
          features: item.features || item.Features || [],
          selectedFeatures:
            item.selectedFeatures || item.SelectedFeatures || [],
          overflowFeaturesCount:
            item.overflowFeaturesCount ||
            item.OverflowFeaturesCount ||
            undefined,
        };
      });
    },
    totalPages() {
      return Math.ceil(this.mappedItems.length / this.effectiveItemsPerPage);
    },
    currentPage() {
      return Math.floor(this.activeItemIndex / this.effectiveItemsPerPage);
    },
    progressStyle() {
      const thumbWidth = this.totalPages > 0 ? 100 / this.totalPages : 100;
      const thumbLeft =
        this.totalPages > 0 ? (this.currentPage / this.totalPages) * 100 : 0;
      return {
        width: `${thumbWidth}%`,
        left: `${thumbLeft}%`,
      };
    },
  },
  mounted() {
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    this.updateResponsiveItemsPerPage();
    window.addEventListener("resize", this.handleResize);

    this.$nextTick(() => {
      this.syncWebComponent();
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
  watch: {
    activeItemIndex() {
      this.syncWebComponent();
    },
  },
  methods: {
    updateResponsiveItemsPerPage() {
      if (this.itemsPerPage !== null) return; // Skip if explicitly set

      const width = window.innerWidth;
      if (width >= 1440) {
        this.responsiveItemsPerPage = 3;
      } else if (width >= 481) {
        this.responsiveItemsPerPage = 2;
      } else {
        this.responsiveItemsPerPage = 1;
      }
    },
    handleResize() {
      this.updateResponsiveItemsPerPage();
    },
    goToPage(pageIndex) {
      const clampedPageIndex = Math.max(
        0,
        Math.min(pageIndex, this.totalPages - 1)
      );
      this.activeItemIndex = clampedPageIndex * this.effectiveItemsPerPage;
    },
    nextPage() {
      if (this.currentPage < this.totalPages - 1) {
        this.activeItemIndex += this.effectiveItemsPerPage;
      }
    },
    previousPage() {
      if (this.currentPage > 0) {
        this.activeItemIndex -= this.effectiveItemsPerPage;
      }
    },
    handleProgressClick(event) {
      const track = event.currentTarget;
      if (!track || this.totalPages <= 1) return;
      const rect = track.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const ratio = Math.min(1, Math.max(0, clickX / rect.width));
      const targetPage = Math.round(ratio * (this.totalPages - 1));
      this.goToPage(targetPage);
    },
    syncWebComponent() {
      this.$nextTick(() => {
        const slideListElement = this.$el.querySelector("slide-list");
        if (!slideListElement) return;

        // Use the slide-list web component's goto() method directly
        if (typeof slideListElement.goto === "function") {
          slideListElement.goto(this.activeItemIndex);
        } else {
          // Fallback: manually scroll the slider if the web component
          // hasn't fully initialized yet
          this.$nextTick(() => {
            const slider = slideListElement.querySelector(".slider");
            const items = slider
              ? slider.querySelectorAll(".slider__item")
              : [];

            if (slider && items.length > 0) {
              const targetItem = items[this.activeItemIndex];
              if (targetItem) {
                const computedStyle = window.getComputedStyle(slider);
                const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
                const scrollPosition = targetItem.offsetLeft - paddingLeft;

                slider.scrollTo({
                  left: Math.max(0, scrollPosition),
                  behavior: this.prefersReducedMotion ? "instant" : "smooth",
                });
              }
            }
          });
        }
      });
    },
  },
};
</script>

<style>
@import "./c-explore-new-projects.css";

.c-explore-new-projects__pagination {
  width: 150px;
  max-width: 150px;
  flex: 0 0 150px;
}

.c-explore-new-projects__progress {
  width: 100%;
  height: 6px;
  background: #c6d1db;
  border-radius: 999px;
  position: relative;
  cursor: pointer;
}

.c-explore-new-projects__progress-fill {
  position: absolute;
  top: 0;
  height: 6px;
  background: #2f597a;
  border-radius: 999px;
  transition: left 0.3s ease;
}
</style>
